import React, { useState, useEffect } from 'react';
import { X, Plus, ArrowRight, Check, Minus, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { track } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VehicleComparison = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Load vehicles for selection
  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles`);
        setVehicles(response.data);
      } catch (error) {
        console.error('Error fetching vehicles:', error);
      }
    };
    fetchVehicles();
  }, []);

  // Load compare list from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('vehicleCompare');
    if (saved) {
      setCompareList(JSON.parse(saved));
    }
  }, []);

  // Save compare list to localStorage
  useEffect(() => {
    localStorage.setItem('vehicleCompare', JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (vehicle) => {
    if (compareList.length < 3 && !compareList.find(v => v.id === vehicle.id)) {
      setCompareList([...compareList, vehicle]);
      track('compare_add', { id: vehicle.id });
    }
    setShowSelector(false);
    setSelectedSlot(null);
  };

  const removeFromCompare = (vehicleId) => {
    setCompareList(compareList.filter(v => v.id !== vehicleId));
  };

  const clearAll = () => {
    setCompareList([]);
  };

  const openSelector = (slot) => {
    setSelectedSlot(slot);
    setShowSelector(true);
  };

  // Comparison specs
  const specs = [
    { key: 'price', label: 'Price', format: (v) => `$${v?.toLocaleString() || 'N/A'}` },
    { key: 'km', label: 'Mileage', format: (v) => `${v?.toLocaleString() || 'N/A'} km` },
    { key: 'year', label: 'Year', format: (v) => v || 'N/A' },
    { key: 'transmission', label: 'Transmission', format: (v) => v || 'N/A' },
    { key: 'drivetrain', label: 'Drivetrain', format: (v) => v || 'N/A' },
    { key: 'fuelType', label: 'Fuel Type', format: (v) => v || 'N/A' },
    { key: 'condition', label: 'Condition', format: (v) => v || 'N/A' },
  ];

  // Find best value for highlighting
  const getBestValue = (key, values) => {
    const validValues = values.filter(v => v !== undefined && v !== null);
    if (validValues.length === 0) return null;
    
    if (key === 'price' || key === 'km') {
      return Math.min(...validValues);
    }
    return null;
  };

  if (!isOpen && compareList.length === 0) return null;

  return (
    <>
      {/* Floating Compare Button */}
      {compareList.length > 0 && !isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-40 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 transition-all hover:scale-105"
          data-testid="compare-button"
        >
          <Scale size={20} />
          Compare ({compareList.length})
        </button>
      )}

      {/* Comparison Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gray-900 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Scale size={24} />
                <h2 className="text-xl font-bold">Compare Vehicles</h2>
                <span className="bg-red-600 px-2 py-1 rounded text-sm">{compareList.length}/3</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={clearAll} className="text-gray-400 hover:text-white text-sm">
                  Clear All
                </button>
                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {compareList.length === 0 ? (
                <div className="text-center py-12">
                  <Scale size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">No vehicles to compare</h3>
                  <p className="text-gray-500 mb-4">Add vehicles from the inventory to compare them side by side</p>
                  <Link to="/inventory">
                    <Button className="bg-red-600 hover:bg-red-700">Browse Inventory</Button>
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    {/* Vehicle Headers */}
                    <thead>
                      <tr>
                        <th className="text-left p-4 bg-gray-50 w-40"></th>
                        {compareList.map((vehicle) => (
                          <th key={vehicle.id} className="p-4 bg-gray-50 min-w-[200px]">
                            <div className="relative">
                              <button
                                onClick={() => removeFromCompare(vehicle.id)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700"
                              >
                                <X size={14} />
                              </button>
                              <img
                                src={vehicle.images?.[0] || '/photos-coming-soon.jpg'}
                                alt={vehicle.title}
                                className="w-full h-32 object-cover rounded-lg mb-3"
                              />
                              <h3 className="font-bold text-gray-800 text-sm">{vehicle.title}</h3>
                              <p className="text-red-600 font-bold">${vehicle.price?.toLocaleString()}</p>
                            </div>
                          </th>
                        ))}
                        {/* Add vehicle slot */}
                        {compareList.length < 3 && (
                          <th className="p-4 bg-gray-50 min-w-[200px]">
                            <button
                              onClick={() => openSelector(compareList.length)}
                              className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:border-red-500 hover:bg-red-50 transition-colors"
                            >
                              <Plus size={32} className="text-gray-400 mb-2" />
                              <span className="text-gray-500 text-sm">Add Vehicle</span>
                            </button>
                          </th>
                        )}
                      </tr>
                    </thead>

                    {/* Specs Comparison */}
                    <tbody>
                      {specs.map((spec, idx) => {
                        const values = compareList.map(v => v[spec.key]);
                        const bestValue = getBestValue(spec.key, values);
                        
                        return (
                          <tr key={spec.key} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="p-4 font-medium text-gray-600">{spec.label}</td>
                            {compareList.map((vehicle) => {
                              const value = vehicle[spec.key];
                              const isBest = bestValue !== null && value === bestValue;
                              
                              return (
                                <td key={vehicle.id} className="p-4 text-center">
                                  <span className={`${isBest ? 'text-green-600 font-bold' : 'text-gray-800'}`}>
                                    {spec.format(value)}
                                    {isBest && <Check size={16} className="inline ml-1" />}
                                  </span>
                                </td>
                              );
                            })}
                            {compareList.length < 3 && <td className="p-4"></td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            {compareList.length > 0 && (
              <div className="bg-gray-100 px-6 py-4 flex justify-between items-center">
                <p className="text-gray-600 text-sm">
                  <Check size={16} className="inline text-green-600 mr-1" />
                  Green highlights indicate best value
                </p>
                <Link to="/contact">
                  <Button className="bg-red-600 hover:bg-red-700 flex items-center gap-2">
                    Inquire About These Vehicles
                    <ArrowRight size={18} />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Vehicle Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <div className="bg-gray-900 text-white px-6 py-4 flex items-center justify-between">
              <h3 className="font-bold">Select a Vehicle to Compare</h3>
              <button onClick={() => setShowSelector(false)} className="p-2 hover:bg-gray-800 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-auto">
              <div className="grid grid-cols-2 gap-4">
                {vehicles
                  .filter(v => !compareList.find(cv => cv.id === v.id))
                  .map((vehicle) => (
                    <button
                      key={vehicle.id}
                      onClick={() => addToCompare(vehicle)}
                      className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-red-500 hover:bg-red-50 transition-colors text-left"
                    >
                      <img
                        src={vehicle.images?.[0] || '/photos-coming-soon.jpg'}
                        alt={vehicle.title}
                        className="w-20 h-14 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">{vehicle.title}</p>
                        <p className="text-red-600 font-bold text-sm">${vehicle.price?.toLocaleString()}</p>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Export a function to add vehicles to compare from other components
export const addVehicleToCompare = (vehicle) => {
  const saved = localStorage.getItem('vehicleCompare');
  const compareList = saved ? JSON.parse(saved) : [];
  
  if (compareList.length < 3 && !compareList.find(v => v.id === vehicle.id)) {
    compareList.push(vehicle);
    localStorage.setItem('vehicleCompare', JSON.stringify(compareList));
    window.dispatchEvent(new Event('compareListUpdated'));
    track('compare_add', { id: vehicle.id });
    return true;
  }
  return false;
};

export default VehicleComparison;
